import { keyframes, styled } from "styled-components";

// Animations
export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

// Styled Components
export const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  animation: ${fadeIn} 0.5s ease-out;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 1rem;
  color: #495057;
  cursor: pointer;
  margin-bottom: 24px;
  transition: all 0.3s ease;

  &:hover {
    background: #e9ecef;
    transform: translateX(-4px);
  }
`;

export const DetailsCard = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

export const GallerySection = styled.div`
  position: relative;
`;

export const MainImage = styled.div`
  width: 100%;
  height: 450px;
  background: linear-gradient(135deg, #e0eafc, #cfdef3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  position: relative;

  @media (max-width: 768px) {
    height: 300px;
  }
`;

export const MainImageItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const ThumbnailGallery = styled.div`
  display: flex;
  gap: 8px;
  padding: 16px;
  overflow-x: auto;
  background: #f8f9fa;
`;

export const Thumbnail = styled.img`
  width: 80px;
  height: 60px;
  object-fit: cover;
  border-radius: 8px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover, &.active {
    border-color: #6c63ff;
    transform: scale(1.05);
  }
`;

export const ExpandButton = styled.button`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    background: #fff;
    transform: scale(1.1);
  }
`;

export const DetailsSection = styled.div`
  padding: 32px;
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: 32px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 768px) {
    padding: 24px;
  }
`;

export const MainDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const PropertyHeader = styled.div`
  border-bottom: 1px solid #e9ecef;
  padding-bottom: 20px;
`;

export const Title = styled.h1`
  font-size: 2.2rem;
  color: #1a1a1a;
  margin-bottom: 8px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

export const Address = styled.p`
  font-size: 1.1rem;
  color: #6c757d;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

export const Price = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #28a745;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const PropertyFeatures = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 16px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
`;

export const Feature = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export const FeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #6c63ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
`;

export const FeatureLabel = styled.span`
  font-size: 0.9rem;
  color: #6c757d;
`;

export const FeatureValue = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #1a1a1a;
`;

export const Description = styled.div`
  line-height: 1.7;
  color: #495057;
`;

export const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: #1a1a1a;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #6c63ff;
`;

export const Sidebar = styled.div`
  background: #f8f9fa;
  border-radius: 16px;
  padding: 24px;
  height: fit-content;
`;

export const ContactForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormTitle = styled.h4`
  font-size: 1.2rem;
  color: #1a1a1a;
  margin-bottom: 8px;
  text-align: center;
`;

export const FormInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #6c63ff;
    box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
  }
`;

export const FormTextarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #6c63ff;
    box-shadow: 0 0 0 3px rgba(108, 99, 255, 0.1);
  }
`;

export const SubmitButton = styled.button`
  background: #6c63ff;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #5a52d5;
    transform: translateY(-2px);
  }
`;

export const ContactOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
`;

export const ContactButton = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
`;

export const WhatsAppButton = styled(ContactButton)`
  background: #25D366;
  color: #fff;

  &:hover {
    background: #128C7E;
    transform: translateY(-2px);
  }
`;

export const CallButton = styled(ContactButton)`
  background: #007bff;
  color: #fff;

  &:hover {
    background: #0056b3;
    transform: translateY(-2px);
  }
`;

export const EmailButton = styled(ContactButton)`
  background: #6c757d;
  color: #fff;

  &:hover {
    background: #5a6268;
    transform: translateY(-2px);
  }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

export const ModalContent = styled.div`
  background: #fff;
  border-radius: 12px;
  max-width: 1000px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.3s ease-out;
`;

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e9ecef;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  color: #1a1a1a;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;

  &:hover {
    color: #1a1a1a;
  }
`;

export const ModalImage = styled.img`
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
`;

export const ViewsCounter = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #666;
  font-size: 14px;
  margin-top: 8px;
  
  svg {
    color: #888;
  }
`;